import { ConfirmationModalComponent } from '$core/modal/confirmation-modal/confirmation-modal.component';
import { LoginModalComponent } from '$core/modal/login-modal/login-modal.component';
import { SubscriptionTypePipe } from '$core/modal/subscription-orders/manage-subscription-order/subscription-type.pipe';
import { Basket } from '$core/models/subscription-order';
import { AuthenticationService } from '$core/services/auth.service';
import { SessionService } from '$core/services/session.service';
import { transition, trigger, useAnimation } from '@angular/animations';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { fadeIn } from 'ng-animate';
import { BsModalService } from 'ngx-bootstrap/modal';
import { first } from 'rxjs/operators';
import { CartService } from './cart.service';

export type { OrderForm, OrderFormData } from './cart-order-form';

interface BasketGroup {
  period: string;
  year: string;
  items: Basket[];
}

@Component({
  selector: 'app-cart',
  templateUrl: './cart.component.html',
  styleUrls: ['./cart-widget/cart-widget.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', useAnimation(fadeIn), {
        params: { timing: 0.2 },
      }),
    ]),
  ],
  providers: [SubscriptionTypePipe],
})
export class CartComponent implements OnInit, OnDestroy {
  public _items: Basket[];
  public hideAlert: boolean;
  public readonly map = {
    virtual: 'VIRTUAL_SUBSCRIPTIONS',
    address: 'TO_SUBSCRIBER_ADDRESS',
    ops_box: 'SUBSCRIPTION_BOX',
    ops: 'POSTE_RESTANTE',
  };

  public get items() {
    return this._items;
  }

  public get ordersCount() {
    let c = 0;
    this.groups.forEach((el) => {
      const tmp: any = {};
      el.items.forEach((item) => {
        tmp[item.delivery_type] = true;
      });
      c = c + Object.keys(tmp).length;
    });
    return c;
  }

  public get isShowAlert(): boolean {
    return !this.hideAlert && this.ordersCount > 1;
  }

  public set items(items) {
    this.groups = [];
    this._items = items;
    this.checkGroup();
  }

  public isLoading$ = this.cartService.isLoading$;
  // form: OrderForm;
  public sub;
  public groups: BasketGroup[] = [];

  public get total() {
    return this.cartService.total;
  }

  constructor(
    public cartService: CartService,
    public sessionService: SessionService,
    private auth: AuthenticationService,
    private router: Router,
    private subscriptionTypePipe: SubscriptionTypePipe,
    private modalService: BsModalService
  ) {
    this.sub = this.cartService.forceUpdate$.subscribe((el) => {
      this._getItems();
    });
  }

  public ngOnInit() {
    this.cartService.payMode = true;
  }

  public pay() {
    if (this.auth.authorizedSnapshot) {
      this._pay();
    } else {
      this.modalService.show(LoginModalComponent, {
        ignoreBackdropClick: true,
        class: 'modal-dialog-centered',
      });
    }
  }

  public clear() {
    this.modalService.show(ConfirmationModalComponent, {
      initialState: {
        title: `DELETE_ALL_CART_ITEMS`,
        declineBtn: 'CANCEL',
      },
      class: 'modal-dialog-centered',
    });

    this.modalService.onHidden.pipe(first()).subscribe((res) => {
      if (res === 'confirm') {
        if (this.auth.authorizedSnapshot) {
          this.isLoading$.next(true);
          this._clear();
        } else {
          this.cartService.clearLocalItems();
          this.items = [];
        }
        this.cartService.total = {
          total: 0,
          total_include_nds: 0,
          count: 0,
        };
      }
    });
  }

  public ngOnDestroy(): void {
    this.cartService.payMode = false;
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  public saveItem(item) {
    this.cartService.updateItem(item, this.items);
  }

  public deleteItem(item) {
    this._deleteItem(item);
  }

  private checkGroup() {
    const group_firs_period = this.subscriptionTypePipe.transform(
      this.items,
      undefined,
      'first'
    );
    const group_second_period = this.subscriptionTypePipe.transform(
      this.items,
      undefined,
      'second'
    );
    if (group_firs_period && group_firs_period.length) {
      this.groups.push({
        period: 'FIRST_HALF_YEAR',
        year: group_firs_period[0].basketable.start_at.slice(0, 4),
        items: group_firs_period,
      });
    }
    if (group_second_period && group_second_period.length) {
      this.groups.push({
        period: 'SECOND_HALF_YEAR',
        year: group_second_period[0].basketable.start_at.slice(0, 4),
        items: group_second_period,
      });
    }
  }

  private async _getItems() {
    this.groups = [];
    try {
      this.items = await this.cartService.getItems();
    } catch (error) {
      this.cartService.onError(error);
    }
  }

  private async _pay() {
    try {
      this.isLoading$.next(true);
      this.isLoading$ = this.cartService.isLoading$;
      const tmp = await this.cartService.pay();
      this.cartService._getTotals();
      this.router.navigate(['/personal/orders'], {
        queryParams: { status: 'pending', order_id: tmp.id },
      });
    } catch (error) {
      this.cartService.onError(error);
    } finally {
      this.isLoading$.next(false);
    }
  }

  private async _clear() {
    try {
      this.isLoading$.next(true);
      await this.cartService.clear();
      this.items = [];
    } catch (error) {
      this.isLoading$.next(false);
      this.cartService.onError(error);
    }
  }

  private async _deleteItem(item: Basket) {
    try {
      await this.cartService.deleteItem([item.id], [item.local_id]);
      this.items = this.items.filter((el) => {
        return (
          (el.id && el.id !== item.id) ||
          (item.local_id && item.local_id !== el.local_id)
        );
      });
      this.cartService.calcTotal();
    } catch (error) {
      console.log(error);

      this.cartService.onError(error);
    }
  }
}
